import React, { useState } from 'react';
import './DocumentHistoryTable.css';

interface DocumentHistoryItem {
  id: number;
  file_name: string;
  uploaded_by: string;
  created_at: string;
  status: string;
  canReplace: boolean;
  is_replacement: boolean;
  replacement_count: number;
}

interface DocumentHistoryTableProps {
  documents: DocumentHistoryItem[];
  loading: boolean;
  error: string | null;
  onReplace: (document: DocumentHistoryItem) => void;
}

const DocumentHistoryTable: React.FC<DocumentHistoryTableProps> = ({
  documents,
  loading,
  error,
  onReplace
}) => {
  const [sortField, setSortField] = useState<keyof DocumentHistoryItem>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: keyof DocumentHistoryItem) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedDocuments = [...documents].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      'Processing': 'status-processing',
      'Email Sent': 'status-sent',
      'Email Failed': 'status-failed',
      'Saved Only': 'status-saved',
      'Replaced': 'status-replaced'
    };

    return (
      <span className={`status-badge ${statusClasses[status as keyof typeof statusClasses] || 'status-default'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="document-history-loading">
        <div className="loading-spinner"></div>
        <span>Loading document history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-history-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="document-history-empty">
        <span className="empty-icon">📄</span>
        <span>No existing documents found for this student and document type.</span>
      </div>
    );
  }

  return (
    <div className="document-history-container">
      <div className="document-history-header">
        <h3>Existing Documents</h3>
        <span className="document-count">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
      </div>
      
      <div className="document-history-table-container">
        <table className="document-history-table">
          <thead>
            <tr>
              <th 
                className={`sortable ${sortField === 'file_name' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSort('file_name')}
              >
                File Name
                <span className="sort-indicator"></span>
              </th>
              <th 
                className={`sortable ${sortField === 'uploaded_by' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSort('uploaded_by')}
              >
                Uploaded By
                <span className="sort-indicator"></span>
              </th>
              <th 
                className={`sortable ${sortField === 'created_at' ? `sorted-${sortDirection}` : ''}`}
                onClick={() => handleSort('created_at')}
              >
                Date & Time
                <span className="sort-indicator"></span>
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedDocuments.map((document) => (
              <tr key={document.id} className={document.is_replacement ? 'replacement-row' : ''}>
                <td className="file-name-cell">
                  <span className="file-name">{document.file_name}</span>
                  {document.is_replacement && (
                    <span className="replacement-indicator" title="This is a replacement document">
                      🔄
                    </span>
                  )}
                  {document.replacement_count > 0 && (
                    <span className="replacement-count" title={`This document has been replaced ${document.replacement_count} time${document.replacement_count !== 1 ? 's' : ''}`}>
                      ({document.replacement_count} replacement{document.replacement_count !== 1 ? 's' : ''})
                    </span>
                  )}
                </td>
                <td>{document.uploaded_by}</td>
                <td>{formatDate(document.created_at)}</td>
                <td>{getStatusBadge(document.status)}</td>
                <td className="actions-cell">
                  {document.canReplace ? (
                    <button
                      className="btn btn-replace"
                      onClick={() => onReplace(document)}
                      title="Replace this document"
                    >
                      Replace
                    </button>
                  ) : (
                    <span className="replace-disabled" title="This document cannot be replaced">
                      Cannot Replace
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentHistoryTable;